import { useAtom, useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Workspace } from '@/features/workspace';
import { NotePromptBox } from '@/components/note-prompt-box';
import { DocumentHeader } from '@/components/document-header';
import { CopyToClipboardBtn } from '../copty-to-clipboard-btn';
import { EditorWorkspace } from '@/features/editor-workspace';
import { TemplateActions } from '@/components/template-actions';
import { LinkedNotesList } from '@/components/linked-notes-list';
import { MedicalNotePrompt } from '@/components/medical-note-prompt';
import { BlockEditorOverlay } from '@/components/block-editor-overlay';
import { SingleSelect } from '@flintmint/web-shared/components/single-select';

import { useAgentSessionState } from '@/hooks/llm';
import { useSearch } from '@/hooks/use-search';
import { useApiRequestState } from '@/hooks/api';
import { useDocuMenu } from '@/hooks/use-docu-menu';
import { usePageTitle } from '@/hooks/use-page-title';
import { useDocumentId } from '@/hooks/use-document-id';
import { useDraftParams } from '@/hooks/use-draft-params';
import { useNote, useNoteWriter, useEnsureTag } from '@/hooks/rxdb-models';
import type { MutationOptions } from '@/hooks/rxdb-base';
import { useStatusFavicon } from '@/hooks/use-status-favicon';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { useSyncStatus } from '@/providers/rxdb/sync-status';

import { appRoutes } from '@/constants/routes';
import { itemTypes } from '@/constants/item-types';
import { FreeAccessBanner } from '@/features/billing/free-access-banner/free-access-banner';
import { TrialEnergyBanner } from '@/features/billing/trial-energy-banner/trial-energy-banner';
import { PaymentIssueBanner } from '@/features/billing/payment-issue-banner/payment-issue-banner';
import { appReadyAtom, editorAtom, emptyAccountAtom, sidebarOverlayAtom } from '@/state';
import { tagColorGreen, tagColorBlue, tagColorPurple, tagColorOrange } from '@flintmint/web-shared/constants/ui';

import { getDocumentIdFromPathname } from '@/utils/helper-utils';

import type { Note } from '@flintmint/core-shared/rxdb';
import type { NoteVariant } from '@/providers/rxdb/constants';
import type { MenuOptionKey } from '@/hooks/use-docu-menu/use-docu-menu';

import styles from './document-workspace.module.scss';

const baseMenuOptions: MenuOptionKey[] = ['duplicate', 'delete'];
const medicalDraftMenuOptions: MenuOptionKey[] = ['delete'];

// NoteWorkspace
export const DocumentWorkspace = memo(() => {
  const [documentTitle, setDocumentTitle] = useState('');

  const appReady = useAtomValue(appReadyAtom);
  const { initialSyncComplete } = useSyncStatus();
  const [sidebarOverlay, setSidebarOverlay] = useAtom(sidebarOverlayAtom);
  const [isEmptyAccount, setIsEmptyAccount] = useAtom(emptyAccountAtom);

  const location = useLocation();
  const navigateWithSearch = useNavigateWithSearch();

  const [, setSearchParams] = useSearchParams();
  const { draftType: newVariant, creatingMedicalNote } = useDraftParams();

  const { id, isDraft } = useDocumentId();

  const editor = useAtomValue(editorAtom);

  const { data: note, isLoading } = useNote(id);
  const { isError, isSuccess } = useApiRequestState(id!);
  const { isLoading: isLLMLoading } = useAgentSessionState(id!);
  const { mutate: upsertNote } = useNoteWriter(note, id, { variant: newVariant, createIfMissing: isDraft });
  const { ensureTag, isReady: tagsReady } = useEnsureTag();

  const menuOptions = creatingMedicalNote ? medicalDraftMenuOptions : baseMenuOptions;
  const [medicalNoteTagIds, setMedicalNoteTagIds] = useState<string[]>([]);

  const { data: userCollectionsData, isLoading: isCollectionsDataLoading } = useSearch({
    where: {
      types: ['notes', 'templates', 'blocks'],
    },
  });

  const noteVariant = (note?.variant || newVariant) as NoteVariant;
  const showTemplateActions = !!note && (noteVariant === 'templates' || (noteVariant === 'notes' && note?.templateId));

  const getMenuData = useDocuMenu(menuOptions);

  usePageTitle(documentTitle);
  useStatusFavicon(isLLMLoading ? 'loading' : isError ? 'error' : isSuccess ? 'success' : 'idle');

  const closeBlockEditor = useCallback(() => setSidebarOverlay(null), [setSidebarOverlay]);
  const blockOverlay = sidebarOverlay?.type === 'blocks' ? sidebarOverlay : null;
  const blockRequestId = blockOverlay?.blockId ?? blockOverlay?.draftId;

  const menu = useMemo(() => {
    if (!note) {
      return [];
    }
    return getMenuData({ type: 'notes', ...note.toJSON() });
  }, [getMenuData, note]);

  useEffect(() => {
    setIsEmptyAccount(!isCollectionsDataLoading && !userCollectionsData.length);
  }, [!!userCollectionsData.length, isCollectionsDataLoading]);

  useEffect(() => {
    return () => {
      setSidebarOverlay(null);
    };
  }, []);

  useEffect(() => {
    if (location.state?.blockId) {
      setSidebarOverlay({
        type: 'blocks',
        blockId: location.state?.blockId,
      });
    }
  }, []);

  useEffect(() => {
    if (!initialSyncComplete || !creatingMedicalNote || note || !tagsReady) return;
    Promise.all([
      ensureTag('Active', tagColorGreen),
      ensureTag('Medical Note', tagColorBlue),
      ensureTag('Patient', tagColorPurple),
      ensureTag('Shift', tagColorOrange),
    ]).then(setMedicalNoteTagIds);
    // deps: !!note tracks existence only; ensureTag is stable (useCallback); id resets tags on draft change
  }, [initialSyncComplete, creatingMedicalNote, !!note, id, tagsReady]);

  // Deep-link not-found: only once sync is complete (dataset is authoritative) and useNote
  // has finished fetching can an absent note be treated as a 404. Never for drafts or
  // in-progress medical-note creation flows. Requires an id: the library route's id param
  // is optional (`/library/:id?`), so the workspace also mounts at plain /library with no
  // id and no note. No dedicated 404 route exists, so redirect to the library.
  useEffect(() => {
    if (!id || isDraft || creatingMedicalNote || isLoading || note || !initialSyncComplete) return;
    console.warn(`[DOCUMENT_WORKSPACE] Note not found after sync, redirecting to library: ${id}`);
    navigateWithSearch(appRoutes.library, { replace: true });
  }, [id, isDraft, creatingMedicalNote, isLoading, note, initialSyncComplete, navigateWithSearch]);

  const handleNoteUpsert = useCallback(
    (payload: Partial<Note>, options?: Pick<MutationOptions, 'ifUnmodifiedSince'>) => {
      return upsertNote(payload, {
        ...options,
        onSuccess: (savedNote) => {
          const activeRouteDocumentId = getDocumentIdFromPathname(window.location.pathname);
          if (activeRouteDocumentId === savedNote.id && isDraft) {
            navigateWithSearch(`${appRoutes.library}/${savedNote.id}`, { replace: true });
          }
        },
      });
    },
    [isDraft, upsertNote, navigateWithSearch]
  );

  // A saved route renders before its note syncs in; the header stays read-only until it does.
  const awaitingSavedNote = !isDraft && !note;

  const handleHeaderUpsert = useCallback(
    (payload: Partial<Note>) => {
      if (awaitingSavedNote) {
        return Promise.resolve();
      }
      if (creatingMedicalNote && !note) {
        if (payload.tagIds) {
          setMedicalNoteTagIds(payload.tagIds);
        }
        return Promise.resolve();
      }
      return handleNoteUpsert(payload);
    },
    [awaitingSavedNote, creatingMedicalNote, note, handleNoteUpsert]
  );

  const onTypeChange = useCallback(
    (variant: string) => {
      if (!note) {
        const next = new URLSearchParams(location.search);
        next.set('draftType', variant);
        return setSearchParams(next, { replace: true });
      }

      if (note && note.variant !== variant) {
        return handleNoteUpsert({ variant });
      }
    },
    [handleNoteUpsert, location.search, note, setSearchParams]
  );

  const typeOptions = useMemo(
    () => [
      {
        ...itemTypes.notes,
        selected: noteVariant === 'notes',
        onClick: () => onTypeChange('notes'),
      },
      {
        ...itemTypes.templates,
        selected: noteVariant === 'templates',
        onClick: () => onTypeChange('templates'),
      },
    ],
    [noteVariant, onTypeChange]
  );

  return (
    <>
      <Workspace>
        {((!isLoading && !!note) || isDraft || (!!id && !note && !initialSyncComplete)) && (
          <EditorWorkspace<Note>
            id={id}
            document={note}
            editorType="notes"
            title={documentTitle}
            autofocus={true}
            autosaveEnabled={!(creatingMedicalNote && !note)}
            draftId={isDraft ? id : undefined}
            onTitleChange={setDocumentTitle}
            onArchived={() => navigateWithSearch(appRoutes.library)}
            upsertDocument={handleNoteUpsert}
            isDocumentLoading={isLoading || (!!id && !note && !isDraft && !initialSyncComplete)}
            data-testid="document-workspace"
            domainSpecificEditor={
              creatingMedicalNote ? (
                <MedicalNotePrompt title={documentTitle} tagIds={note?.tagIds ?? medicalNoteTagIds} upsertNote={upsertNote} />
              ) : null
            }
            header={
              <div className={styles.header}>
                <PaymentIssueBanner />
                <FreeAccessBanner />
                <TrialEnergyBanner />
                <DocumentHeader
                  menu={menu}
                  document={note}
                  tagIds={creatingMedicalNote ? medicalNoteTagIds : undefined}
                  name={documentTitle}
                  documentExists={!!note}
                  disabled={awaitingSavedNote}
                  upsertDocument={handleHeaderUpsert}
                  onChange={setDocumentTitle}
                  lastEditTime={note?.updatedAt}
                  rightActions={
                    showTemplateActions &&
                    note && (
                      <TemplateActions
                        note={note}
                        variant={noteVariant}
                        templateId={note.templateId!}
                        onEditSourceTemplate={(template) => {
                          navigateWithSearch(`${appRoutes.library}/${template.id}`, {
                            state: {
                              linkedInstanceNote: note.toJSON() as Note,
                            },
                          });
                        }}
                      />
                    )
                  }
                  leftActions={
                    <div className={styles.documentLeftActions}>
                      <SingleSelect
                        disabled={creatingMedicalNote}
                        options={typeOptions}
                        iconSize={20}
                        className={styles.noteTypeDropdown}
                        data-testid="type-select"
                      />
                      {showTemplateActions && noteVariant === 'templates' && <LinkedNotesList templateId={note.id} />}
                      {editor && <CopyToClipboardBtn editor={editor} />}
                    </div>
                  }
                />
              </div>
            }
          />
        )}
      </Workspace>
      {appReady && (
        <>
          {(!isEmptyAccount || id) && !creatingMedicalNote && (isDraft || !id || !!note || initialSyncComplete) && (
            // Hide NotePromptBox's LlmChat when BlockEditorOverlay is open to prevent
            // duplicate chat panels (BlockEditorOverlay renders its own LlmChat)
            <NotePromptBox hideLlmChat={!!blockOverlay} />
          )}
          <BlockEditorOverlay
            open={!!blockOverlay}
            requestId={blockRequestId}
            blockId={blockOverlay?.blockId}
            onClose={closeBlockEditor}
            context={blockOverlay?.context}
          />
        </>
      )}
    </>
  );
});
